import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { useAuth } from '../context/AuthContext';
import {
  meetingService,
  MeetingMemberStatus,
  MeetingMemberPreferences,
} from '../services/meetingService';
import {
  loadWorkshop2026Context,
  formatWorkshopSchedule,
  formatFirstSessionDate,
  WORKSHOP_2026_PATH,
} from '../services/workshop2026Service';
import { ProfileService } from '../services/profileService';
import { Meeting } from '../services/meetingService';
import Workshop2026AboutTab from '../components/workshop2026/Workshop2026AboutTab';
import Workshop2026WorkshopTab from '../components/workshop2026/Workshop2026WorkshopTab';
import WorkshopInterestModal from '../components/workshop2026/WorkshopInterestModal';
import StepWorkDiscoveryBanner from '../components/StepWorkDiscoveryBanner';
import { interestSurveyToPayload } from '../data/workshop2026InterestSurvey';
import { useDialog } from '../context/DialogContext';
import { useProfileModal } from '../context/ProfileModalContext';
import styles from '../styles/workshop2026Page.module.css';

type WorkshopTab = 'about' | 'workshop';

const DEFAULT_PREFERENCES: MeetingMemberPreferences = {
  include_on_printed_phone_list: false,
  include_email_on_list: false,
  name_list_mode: 'when_workshop_starts',
  sponsor_willingness: 'no',
  display_preference: 'name_only',
  email_notifications_enabled: true,
};

interface Workshop2026PageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
  onCreateAccount?: () => void;
}

const Workshop2026Page: React.FC<Workshop2026PageProps> = ({
  user,
  onLogout,
  onLogin,
  onCreateAccount,
}) => {
  const { user: authUser } = useAuth();
  const { showConfirm, showAlert } = useDialog();
  const { isProfileModalOpen } = useProfileModal();
  const profileModalWasOpen = useRef(false);
  const effectiveUser = user || authUser;
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [meetingCode, setMeetingCode] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [memberStatus, setMemberStatus] = useState<MeetingMemberStatus | null>(null);
  const [profilePhone, setProfilePhone] = useState('');
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [withdrawingInterest, setWithdrawingInterest] = useState(false);
  const [confirmingAttendance, setConfirmingAttendance] = useState(false);

  const getInitialTab = (): WorkshopTab => {
    const t = searchParams.get('tab');
    if (t === 'workshop') return 'workshop';
    return 'about';
  };

  const [activeTab, setActiveTab] = useState<WorkshopTab>(getInitialTab());

  usePageTracking('2026 SAA Step Workshop');

  useEffect(() => {
    const tabFromUrl = getInitialTab();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const refreshMeeting = useCallback(async (authed = false) => {
    const ctx = await loadWorkshop2026Context({ authed });
    setMeeting(ctx.meeting);
    setMeetingCode(ctx.meetingCode);
    setLoadError(ctx.loadError);
    return ctx;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await refreshMeeting(!!effectiveUser);
      if (cancelled) return;
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [effectiveUser, refreshMeeting]);

  const refreshMemberStatus = useCallback(async () => {
    if (!effectiveUser || !meetingCode) {
      setMemberStatus(null);
      return;
    }
    try {
      const status = await meetingService.getMemberStatus(meetingCode);
      setMemberStatus(status);
    } catch {
      setMemberStatus(null);
    }
  }, [effectiveUser, meetingCode]);

  useEffect(() => {
    refreshMemberStatus();
  }, [refreshMemberStatus]);

  useEffect(() => {
    if (!effectiveUser) {
      setProfilePhone('');
      return;
    }
    ProfileService.getUserProfile()
      .then((p) => setProfilePhone(p.phone || ''))
      .catch(() => setProfilePhone(''));
  }, [effectiveUser]);

  useEffect(() => {
    if (profileModalWasOpen.current && !isProfileModalOpen && effectiveUser) {
      ProfileService.getUserProfile()
        .then((p) => setProfilePhone(p.phone || ''))
        .catch(() => setProfilePhone(''));
    }
    profileModalWasOpen.current = isProfileModalOpen;
  }, [isProfileModalOpen, effectiveUser]);

  useEffect(() => {
    const reg = memberStatus?.registration_state;
    if (effectiveUser && meetingCode && (reg === 'interest' || reg === 'confirmed')) {
      refreshMeeting(true);
    }
  }, [memberStatus?.registration_state, effectiveUser, meetingCode, refreshMeeting]);

  const handleTabClick = (tab: WorkshopTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleJoinInterest = () => {
    if (!meetingCode) return;
    setInterestModalOpen(true);
  };

  const handleInterestSubmit = async (payload: ReturnType<typeof interestSurveyToPayload>) => {
    if (!meetingCode) throw new Error('Workshop not configured');
    const result = await meetingService.registerWorkshopInterest(meetingCode, payload);
    await refreshMemberStatus();
    await refreshMeeting(true);
    setActiveTab('workshop');
    setSearchParams({ tab: 'workshop' });
    const email = effectiveUser?.email || '';
    const emailNote =
      result.confirmation_email_sent && email
        ? ` We sent a confirmation email to ${email} with a link back to this page.`
        : email
          ? ` We'll keep you informed via email at ${email}.`
          : " We'll keep you informed via email.";
    showAlert({
      title: "You're on the interest list",
      message: `Your interest in the 2026 SAA Step Workshop has been recorded.${emailNote}`,
      variant: 'success',
    });
  };

  const handleConfirmAttendance = () => {
    if (!meetingCode || confirmingAttendance) return;
    showConfirm({
      title: "Confirm you're planning to attend?",
      message:
        'This lets the facilitators know you intend to join the 2026 SAA Workshop. The Zoom link will appear on this tab when it is published.',
      confirmText: "I'm planning to attend",
      cancelText: 'Not yet',
      variant: 'info',
      onConfirm: async () => {
        setConfirmingAttendance(true);
        try {
          const result = await meetingService.confirmWorkshopAttendance(meetingCode);
          await refreshMemberStatus();
          const ctx = await refreshMeeting(true);
          setActiveTab('workshop');
          setSearchParams({ tab: 'workshop' });
          const email = effectiveUser?.email || '';
          const emailNote =
            result.confirmation_email_sent && email
              ? ` We sent a confirmation email to ${email} with a link back to this page.`
              : '';
          showAlert({
            title: "You're confirmed",
            message: `You're confirmed for the 2026 SAA Step Workshop. First session: ${formatFirstSessionDate(ctx.meeting)}.${emailNote} The Zoom link will appear on the Workshop tab when it is ready.`,
            variant: 'success',
          });
        } catch (err) {
          showAlert({
            message: err instanceof Error ? err.message : 'Could not confirm attendance.',
            variant: 'danger',
          });
        } finally {
          setConfirmingAttendance(false);
        }
      },
    });
  };

  const handleSavePreferences = async (updates: Partial<MeetingMemberPreferences>) => {
    if (!meetingCode) return;
    const saved = await meetingService.updateMyMemberPreferences(meetingCode, updates);
    setMemberStatus((prev) => (prev ? { ...prev, ...saved } : prev));
  };

  const handleSaveProfilePhone = async (phone: string) => {
    await ProfileService.updateUserProfile({ phone });
    setProfilePhone(phone);
  };

  const handleLeaveWorkshop = () => {
    if (!meetingCode || withdrawingInterest) return;
    const isConfirmed = memberStatus?.registration_state === 'confirmed';
    showConfirm({
      title: isConfirmed ? 'Leave the 2026 SAA Workshop?' : 'Leave interest list?',
      message: isConfirmed
        ? 'You will be removed from the participant roster and contact lists for the 2026 SAA Workshop. Leaving only affects your workshop membership — your step work in Recovery Commons stays with you. You can join again anytime.'
        : 'You will be removed from the interest list and will stop receiving updates about the 2026 SAA Workshop. You can join again anytime.',
      confirmText: isConfirmed ? 'Leave workshop' : 'Remove me',
      cancelText: isConfirmed ? 'Stay registered' : 'Keep me on the list',
      variant: 'danger',
      onConfirm: async () => {
        setWithdrawingInterest(true);
        try {
          await meetingService.withdrawWorkshopInterest(meetingCode);
          await refreshMemberStatus();
          await refreshMeeting(true);
        } catch (err) {
          showAlert({
            message: err instanceof Error ? err.message : 'Could not remove you from the workshop.',
            variant: 'danger',
          });
        } finally {
          setWithdrawingInterest(false);
        }
      },
    });
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '2026 Step Workshop', path: WORKSHOP_2026_PATH, active: true },
  ];

  const scheduleLine = formatWorkshopSchedule(meeting);
  const firstSession = formatFirstSessionDate(meeting);
  const displayName = meeting?.name || '2026 SAA Step Workshop';
  const showAdminConfigWarning = !!(effectiveUser?.isAdmin || memberStatus?.is_admin);
  const userDisplayName =
    effectiveUser?.name || effectiveUser?.display_name || effectiveUser?.displayName || '';

  const registrationState = memberStatus?.registration_state ?? 'none';
  const memberPreferences: MeetingMemberPreferences = memberStatus
    ? {
        include_on_printed_phone_list: memberStatus.include_on_printed_phone_list,
        include_email_on_list: memberStatus.include_email_on_list,
        name_list_mode: memberStatus.name_list_mode,
        sponsor_willingness: memberStatus.sponsor_willingness,
        display_preference: memberStatus.display_preference,
        email_notifications_enabled: memberStatus.email_notifications_enabled,
      }
    : DEFAULT_PREFERENCES;

  if (loading) {
    return (
      <div className={styles.workshopPage}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.mainContent}>
          <div className={styles.loading}>Loading workshop…</div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.workshopPage}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />

      <main className={styles.mainContent}>
        {showAdminConfigWarning && !meetingCode && (
          <div className={styles.configWarning}>
            Workshop backend not linked yet. Set the meeting code in Admin → Home
            (e.g. after creating a workshop record).
          </div>
        )}
        {showAdminConfigWarning && loadError && (
          <div className={styles.configWarning}>
            Could not load workshop record ({meetingCode}): {loadError}
          </div>
        )}

        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>{displayName}</h1>
          <p className={styles.heroSubtitle}>
            Personally work your SAA 12 steps in community with others · Online
          </p>
          <p className={styles.heroSchedule}>
            {scheduleLine} · Starting {firstSession}
          </p>
          {meeting?.description && (
            <p className={styles.heroSchedule} style={{ marginTop: '0.4rem' }}>
              {meeting.description}
            </p>
          )}
        </section>

        {effectiveUser && registrationState === 'confirmed' && (
          <div className={styles.statusStrip}>
            <p className={styles.statusStripText}>You&apos;re confirmed for the 2026 SAA Workshop.</p>
            <p className={styles.statusStripSub}>First session: {firstSession}.</p>
          </div>
        )}

        {effectiveUser && (
          <StepWorkDiscoveryBanner user={effectiveUser} dismissKeySuffix="-workshop2026" />
        )}

        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'about' ? styles.activeTab : ''}`}
            onClick={() => handleTabClick('about')}
          >
            About
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'workshop' ? styles.activeTab : ''}`}
            onClick={() => handleTabClick('workshop')}
          >
            Workshop
          </button>
        </div>

        <div className={styles.tabPanel}>
          {activeTab === 'about' && (
            <Workshop2026AboutTab
              isSignedIn={!!effectiveUser}
              registrationState={registrationState}
              firstSessionLabel={firstSession}
              onGoToWorkshopTab={() => handleTabClick('workshop')}
              onLoginForQuestion={onLogin}
              userEmail={effectiveUser?.email}
              userDisplayName={userDisplayName}
            />
          )}
          {activeTab === 'workshop' && (
            <>
              {!effectiveUser ? (
                <div className={styles.workshopJoinBlock}>
                  <p className={styles.workshopJoinLead}>
                    Create a free Recovery Commons account to join the interest list for this
                    workshop. Already have an account? Sign in.
                  </p>
                  <div className={styles.statusActions}>
                    {onCreateAccount && (
                      <button type="button" className={styles.primaryBtn} onClick={onCreateAccount}>
                        Create account
                      </button>
                    )}
                    <button type="button" className={styles.secondaryBtn} onClick={onLogin}>
                      Sign in
                    </button>
                  </div>
                  <p className={styles.workshopJoinDetail}>
                    After you join the interest list and confirm you&apos;re planning to attend,
                    this tab becomes your workshop home: Zoom link, calendar, phone list, and
                    updates.
                  </p>
                </div>
              ) : (
                <Workshop2026WorkshopTab
                  registrationState={registrationState}
                  meeting={meeting}
                  meetingCode={meetingCode}
                  memberStatus={memberStatus}
                  interestAt={memberStatus?.interest_at}
                  memberPreferences={memberPreferences}
                  profilePhone={profilePhone}
                  userEmail={effectiveUser?.email}
                  onJoinInterestList={handleJoinInterest}
                  onConfirmAttendance={handleConfirmAttendance}
                  onLeaveWorkshop={handleLeaveWorkshop}
                  onSavePreferences={handleSavePreferences}
                  onSavePhone={handleSaveProfilePhone}
                  confirming={confirmingAttendance}
                  leaving={withdrawingInterest}
                  meetingCodeMissing={!meetingCode}
                />
              )}
            </>
          )}
        </div>

        {effectiveUser && memberStatus?.is_admin && meetingCode && (
          <div className={styles.adminStripBottom}>
            <span>You&apos;re the workshop admin.</span>
            <a href={`/meetings/${meetingCode}/settings`} className={styles.adminStripLink}>
              Workshop settings →
            </a>
          </div>
        )}
      </main>

      <WorkshopInterestModal
        isOpen={interestModalOpen}
        onClose={() => setInterestModalOpen(false)}
        onSubmit={handleInterestSubmit}
      />
    </div>
  );
};

export default Workshop2026Page;
